import { createContext, createElement, useContext, useEffect, useState, ReactNode } from 'react';
import { FALLBACK_ONTARIO_HOSPITAL_DOMAINS } from '../config/ontarioHospitalDomains';
import { fetchOhpHospitalDirectory } from '../services/hospitalEmailDomainApi';
import type { OhpHospitalSite } from '../types/hospitalEmailDomain';

interface HospitalOhpDomainContextValue {
  domains: readonly string[];
  sites: readonly OhpHospitalSite[];
  isLoading: boolean;
}

const HospitalOhpDomainContext = createContext<HospitalOhpDomainContextValue>({
  domains: FALLBACK_ONTARIO_HOSPITAL_DOMAINS,
  sites: [],
  isLoading: true,
});

export function HospitalOhpDomainProvider({ children }: { children: ReactNode }) {
  const [domains, setDomains] = useState<readonly string[]>(FALLBACK_ONTARIO_HOSPITAL_DOMAINS);
  const [sites, setSites] = useState<readonly OhpHospitalSite[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    fetchOhpHospitalDirectory()
      .then((directory) => {
        if (cancelled) return;
        setDomains(directory.domains.length > 0 ? directory.domains : FALLBACK_ONTARIO_HOSPITAL_DOMAINS);
        setSites(directory.sites);
      })
      .catch(() => {
        if (cancelled) return;
        setDomains(FALLBACK_ONTARIO_HOSPITAL_DOMAINS);
        setSites([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return createElement(HospitalOhpDomainContext.Provider, { value: { domains, sites, isLoading } }, children);
}

export function useHospitalOhpDomains(): HospitalOhpDomainContextValue {
  return useContext(HospitalOhpDomainContext);
}
